import api from '@pray/shared/utils/api';
import type { ObjectResponse } from '@pray/shared/utils/api/types/response';

import type { YouTubeConnectionStatusData } from './getYouTubeConnectionStatus';

/* eslint-disable camelcase */
export type YouTubeOAuthStartResponse = ObjectResponse<{
  oauth_url: string;
  state: string;
}>;

export type YouTubeOAuthCallbackResponse = ObjectResponse<{
  is_connected: boolean;
  profile_handle?: string;
  profile_image_url?: string;
  profile_url?: string;
  channel_id?: string;
  error?: string;
}>;

export type YouTubeDisconnectResponse = ObjectResponse<YouTubeConnectionStatusData>;

export type YouTubePlaylistsResponse = ObjectResponse<{
  playlists: YouTubePlaylist[];
  total_count: number;
  next_page_token?: string;
}>;

export type YouTubePlaylist = {
  id: string;
  title: string;
  description: string;
  thumbnail_url?: string;
  video_count: number;
  privacy_status: 'public' | 'private' | 'unlisted';
  published_at: string;
  updated_at: string;
};

export type YouTubeVideosResponse = ObjectResponse<{
  videos: YouTubeVideo[];
  total_count: number;
  next_page_token?: string;
}>;

export type YouTubeVideo = {
  id: string;
  title: string;
  description: string;
  thumbnail_url?: string;
  duration?: string;
  published_at: string;
  view_count?: number;
  like_count?: number;
  privacy_status: 'public' | 'private' | 'unlisted';
  playlist_id?: string;
};
/* eslint-enable camelcase */

/**
 * Initiates YouTube OAuth flow and returns the OAuth URL
 */
export async function startYouTubeOAuth({ artistId }: { artistId: string }): Promise<YouTubeOAuthStartResponse> {
  if (!artistId) throw new Error('Artist ID is required');

  const url = `/web/studio/artists/${artistId}/socials/youtube/oauth/start`;
  const response = await api.executePostRequest<YouTubeOAuthStartResponse>(url, {});

  return response.data;
}

/**
 * Handles YouTube OAuth callback with authorization code
 */
export async function handleYouTubeOAuthCallback({
  artistId,
  code,
  state,
}: {
  artistId: string;
  code: string;
  state: string;
}): Promise<YouTubeOAuthCallbackResponse> {
  if (!artistId) throw new Error('Artist ID is required');
  if (!code) throw new Error('Authorization code is required');
  if (!state) throw new Error('State parameter is required');

  const url = `/web/studio/artists/${artistId}/socials/youtube/oauth/callback`;
  const response = await api.executePostRequest<YouTubeOAuthCallbackResponse>(url, {
    code,
    state,
  });

  return response.data;
}

/**
 * Disconnects YouTube account for the artist
 */
export async function disconnectYouTubeAccount({
  artistId,
}: {
  artistId: string;
}): Promise<YouTubeConnectionStatusData> {
  if (!artistId) throw new Error('Artist ID is required');

  const url = `/web/studio/artists/${artistId}/socials/youtube/disconnect`;
  const response = await api.executeDeleteRequest<YouTubeDisconnectResponse>(url);

  return response.data.data;
}

/**
 * Fetches YouTube playlists for the connected account
 */
export async function getYouTubePlaylists({
  artistId,
  maxResults = 25,
  pageToken,
}: {
  artistId: string;
  maxResults?: number;
  pageToken?: string;
}): Promise<YouTubePlaylistsResponse> {
  if (!artistId) throw new Error('Artist ID is required');

  const url = `/web/studio/artists/${artistId}/socials/youtube/playlists`;
  const params: Record<string, string | number> = { maxResults };
  if (pageToken) params.pageToken = pageToken;

  const response = await api.executeGetRequest<YouTubePlaylistsResponse>(url, params);

  return response.data;
}

/**
 * Fetches YouTube videos for the connected account
 */
export async function getYouTubeVideos({
  artistId,
  playlistId,
  maxResults = 25,
  pageToken,
}: {
  artistId: string;
  playlistId?: string;
  maxResults?: number;
  pageToken?: string;
}): Promise<YouTubeVideosResponse> {
  if (!artistId) throw new Error('Artist ID is required');

  const url = `/web/studio/artists/${artistId}/socials/youtube/videos`;
  const params: Record<string, string | number> = { maxResults };
  if (playlistId) params.playlistId = playlistId;
  if (pageToken) params.pageToken = pageToken;

  const response = await api.executeGetRequest<YouTubeVideosResponse>(url, params);

  return response.data;
}
